import { Tabs } from '@mui/material'
import { MainApi } from 'api/MainApi'
import { MissionJobRead, OdysseyRead } from 'api/schema'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import Loader from 'components/Loader/Loader'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import MissionStats from 'components/MissionStats/MissionStats'
import GausiumMapPreviewOverlay from 'components/GausiumMapPreviewOverlay/GausiumMapPreviewOverlay'
import ScenarioTable from 'components/ScenarioTable/ScenarioTable'
import SharedMissionItem from 'components/SharedMissionItem/SharedMissionItem'
import StationsOnMap from 'components/StationsOnMap/StationsOnMap'
import { StyledTab } from 'components/StyledTab/StyledTab'
import Title from 'components/Title/Title'
import { Feature, Point } from 'geojson'
import CleaningPointsOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningPointsOnMap'
import CleaningRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningRoutesOnMap'
import CleaningZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningZonesOnMap'
import MissionStartFinishPoints from 'pages/Mission/MissionView/MissionMap/Layers/MissionStartFinishPoints'
import RestrictZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/RestrictZonesOnMap'
import TransferRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/TransferRoutesOnMap'
import AllRobotsOnMap from 'pages/RobotList/RobotListView/AllRobotsOnMap/AllRobotsOnMap'
import RobotItem from 'pages/RobotList/RobotListView/RobotItem/RobotItem'
import TaskSettingsModal from 'pages/TaskScheduler/TaskSettingsModal/TaskSettingsModal'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapboxMap } from 'react-map-gl'
import { useNavigate } from 'react-router-dom'
import { PROGRESS_TYPES } from 'utils/constants'
import { IMapboxElement, IMission, IMissionInfo, IRobotInfo, IStation } from 'utils/interfaces'
import { zoomMapTo } from 'utils/mapUtils'

import FacilityCleaningTaskItem from './FacilityCleaningTaskItem/FacilityCleaningTaskItem'

import style from './FacilityView.module.css'

const PAGE_SIZE = 20

// Global promise cache to deduplicate fetches across components and remounts
const missionPromises: Record<number, Promise<IMission>> = {}
const FacilityView = ({
  facility,
  robots,
  stations,
  onRobotsUpdated,
  onStationsChanged,
  activeTab,
  setActiveTab,
}: {
  facility: any
  robots: IRobotInfo[]
  stations: IStation[]
  onRobotsUpdated: () => void
  onStationsChanged: () => void
  activeTab: number
  setActiveTab: (tab: number) => void
}) => {
  const { t } = useTranslation()
  const [highlightedRobotId, setHighlightedRobotId] = useState<number | undefined>(undefined)
  const [highlightedMissionIds, setHighlightedMissionIds] = useState<number[]>([])
  const mapRef = useRef<IMapboxElement>(null)
  const [map, setMap] = useState<MapboxMap | undefined>(undefined)

  const [selectedMissionsDetails, setSelectedMissionsDetails] = useState<IMission[]>([])
  const [selectedScheduledMissionIds, setSelectedScheduledMissionIds] = useState<Set<number>>(new Set())
  const [scheduledMissionsDetails, setScheduledMissionsDetails] = useState<IMission[]>([])
  const [referencedScenarios, setReferencedScenarios] = useState<any[]>([])
  const missionCacheRef = useRef<Record<number, IMission>>({})
  const [cacheVersion, setCacheVersion] = useState(0)

  const hasInitializedCleaningMissions = useRef(false)
  const lastZoomedMissionIds = useRef<string>('')
  const hasZoomedAllCleaning = useRef(false)

  useEffect(() => {
    setHighlightedMissionIds([])
    setHighlightedRobotId(undefined)
    setSelectedMissionsDetails([])
    lastZoomedMissionIds.current = ''
    hasZoomedAllCleaning.current = false
  }, [activeTab])

  useEffect(() => {
    const fetchMissionDetails = async () => {
      if ((activeTab === 0 || activeTab === 2 || activeTab === 3) && highlightedMissionIds.length > 0) {
        try {
          const promises = highlightedMissionIds.map(id => {
            if (missionCacheRef.current[id]) {
              return Promise.resolve(missionCacheRef.current[id])
            }
            if (!missionPromises[id]) {
              missionPromises[id] = MainApi.fetchMission(id)
            }
            return missionPromises[id]
          })
          const results = await Promise.all(promises)

          let updated = false
          results.forEach(m => {
            if (!missionCacheRef.current[m.mission.id]) {
              missionCacheRef.current[m.mission.id] = m
              updated = true
            }
          })
          if (updated) setCacheVersion(v => v + 1)

          setSelectedMissionsDetails(results)
        } catch (error) {
          console.error('Failed to fetch selected missions', error)
        }
      } else {
        setSelectedMissionsDetails([])
      }
    }
    fetchMissionDetails()
  }, [highlightedMissionIds, activeTab])

  useEffect(() => {
    const fetchMultipleMissions = async () => {
      if (activeTab === 0 && selectedScheduledMissionIds.size > 0) {
        try {
          const promises = Array.from(selectedScheduledMissionIds).map(id => {
            if (missionCacheRef.current[id]) {
              return Promise.resolve(missionCacheRef.current[id])
            }
            if (!missionPromises[id]) {
              missionPromises[id] = MainApi.fetchMission(id)
            }
            return missionPromises[id]
          })
          const results = await Promise.all(promises)

          let updated = false
          results.forEach(m => {
            if (!missionCacheRef.current[m.mission.id]) {
              missionCacheRef.current[m.mission.id] = m
              updated = true
            }
          })
          if (updated) setCacheVersion(v => v + 1)

          setScheduledMissionsDetails(results)
        } catch (error) {
          console.error('Failed to fetch multiple missions', error)
        }
      } else {
        setScheduledMissionsDetails([])
      }
    }
    fetchMultipleMissions()
  }, [selectedScheduledMissionIds, activeTab])

  useEffect(() => {
    if (map && selectedMissionsDetails.length > 0 && (activeTab === 0 || activeTab === 2 || activeTab === 3)) {
      const currentIdsStr = selectedMissionsDetails
        .map(m => m.mission.id)
        .sort()
        .join(',')
      if (lastZoomedMissionIds.current !== currentIdsStr) {
        let features: Feature[] = []
        if (activeTab === 0) {
          features = selectedMissionsDetails.flatMap(m => m.cleaningRoutes)
        } else {
          features = selectedMissionsDetails.flatMap(m => [
            ...m.cleaningZones,
            ...m.restrictZones,
            ...m.cleaningRoutes,
            ...m.transferRoutes,
            ...(m.pointRoutes || []),
          ])
        }

        if (features.length > 0) {
          zoomMapTo(map, features)
          lastZoomedMissionIds.current = currentIdsStr
        }
      }
    } else if (map && activeTab === 0 && scheduledMissionsDetails.length > 0 && !hasZoomedAllCleaning.current) {
      const features = scheduledMissionsDetails.flatMap(m => [...m.cleaningZones, ...m.cleaningRoutes])
      if (features.length > 0) {
        zoomMapTo(map, features)
        hasZoomedAllCleaning.current = true
      }
    }
  }, [map, selectedMissionsDetails, scheduledMissionsDetails, activeTab])

  // Missions tab state
  const [missions, setMissions] = useState<IMissionInfo[]>([])
  const tasks: MissionJobRead[] = useMemo(() => robots.flatMap(r => (r.robotRaw as any).missions_jobs || []), [robots])
  const odysseys: OdysseyRead[] = useMemo(() => robots.flatMap(r => (r.robotRaw as any).odysseys || []), [robots])
  const [missionsTotalCount, setMissionsTotalCount] = useState(0)
  const [missionsProgress, setMissionsProgress] = useState(PROGRESS_TYPES.IDLE)
  const [missionsPage, setMissionsPage] = useState(1)
  const [missionsPageInput, setMissionsPageInput] = useState('1')
  const [missionsRefreshKey, setMissionsRefreshKey] = useState(0)

  // Cleaning tasks calculation
  const scheduledTasks = useMemo(() => {
    const tasks: (OdysseyRead & {
      name?: string
      missions: number[]
      overall_length?: number
      overall_cleaned_area?: number
    })[] = []
    odysseys.forEach(odyssey => {
      if (odyssey.job_type === 255) return
      const weekdays = odyssey.scheduler_settings?.weekdays
      if (weekdays && Object.values(weekdays).some(val => val && val.trim().length > 0)) {
        let displayName = odyssey.name || undefined
        let missionIds: number[] = odyssey.missions || []

        let overallLength = 0
        let overallArea = 0

        if (odyssey.scenario) {
          const matchedScenario = referencedScenarios.find(s => s.id === odyssey.scenario)
          if (matchedScenario) {
            displayName = matchedScenario.name || undefined
            missionIds = matchedScenario.missions_ids || []
            overallLength = matchedScenario.overall_length || 0
            overallArea = matchedScenario.overall_cleaned_area || 0
          }
        } else if (missionIds.length === 1) {
          const mId = missionIds[0]
          const matchedMission = scheduledMissionsDetails.find(m => m.mission.id === mId)
          if (matchedMission) {
            displayName = matchedMission.mission.name
            overallLength = matchedMission.mission.overall_length || 0
            overallArea = matchedMission.mission.overall_cleaned_area || 0
          }
        }

        tasks.push({
          ...odyssey,
          name: displayName,
          missions: missionIds,
          overall_length: overallLength,
          overall_cleaned_area: overallArea,
        })
      }
    })
    tasks.sort((a, b) => {
      const getTime = (t: OdysseyRead) => {
        if (!t.scheduler_settings?.datetime) return Infinity
        const dt = new Date(t.scheduler_settings.datetime.replace(' ', 'T'))
        const time = dt.getHours() * 60 + dt.getMinutes()
        return isNaN(time) ? Infinity : time
      }
      return getTime(a) - getTime(b)
    })

    const overlaps = new Set<number>()
    const robotTasks: Record<number, any[]> = {}
    tasks.forEach(t => {
      if (t.robot_id) {
        if (!robotTasks[t.robot_id]) robotTasks[t.robot_id] = []
        robotTasks[t.robot_id].push(t)
      }
    })

    const getTimeInMinutes = (datetime?: string | null) => {
      if (!datetime) return Infinity
      const dt = new Date(datetime.replace(' ', 'T'))
      const time = dt.getHours() * 60 + dt.getMinutes()
      return isNaN(time) ? Infinity : time
    }

    Object.values(robotTasks).forEach(rTasks => {
      for (let i = 0; i < rTasks.length; i++) {
        for (let j = i + 1; j < rTasks.length; j++) {
          const a = rTasks[i]
          const b = rTasks[j]

          const aDays = a.scheduler_settings?.weekdays || {}
          const bDays = b.scheduler_settings?.weekdays || {}
          const sharedDay = Object.keys(aDays).some(day => aDays[day] === 'true' && bDays[day] === 'true')

          if (sharedDay) {
            const aStart = getTimeInMinutes(a.scheduler_settings?.datetime)
            const aDuration = (a.average_statistics?.duration_overall || a.execution_time || 0) / 60
            const aEnd = aStart + aDuration

            const bStart = getTimeInMinutes(b.scheduler_settings?.datetime)
            const bDuration = (b.average_statistics?.duration_overall || b.execution_time || 0) / 60
            const bEnd = bStart + bDuration

            if (aStart < bEnd && bStart < aEnd) {
              overlaps.add(a.id)
              overlaps.add(b.id)
            }
          }
        }
      }
    })

    return tasks.map(t => ({ ...t, isOverlapping: overlaps.has(t.id) }))
  }, [odysseys, referencedScenarios, scheduledMissionsDetails])

  const aggregateStats = useMemo(() => {
    let coverage = 0
    let length = 0
    let time = 0

    // For aggregate, we sum up all scheduled tasks (Odysseys)
    scheduledTasks.forEach(task => {
      const stats = task.average_statistics
      if (stats) {
        coverage += stats.cleaned_area_actual || task.overall_cleaned_area || 0
        length += stats.travel_distance_actual || task.overall_length || 0
        time += stats.duration_overall || task.execution_time || 0
      } else {
        coverage += task.overall_cleaned_area || 0
        length += task.overall_length || 0
        time += task.execution_time || 0
      }
    })
    return { coverage, length, time }
  }, [scheduledTasks])

  const isAllCleaningSelected = useMemo(() => {
    if (scheduledTasks.length === 0) return false
    const allIds = scheduledTasks.flatMap(t => t.missions)
    return allIds.length > 0 && allIds.every(id => selectedScheduledMissionIds.has(id))
  }, [scheduledTasks, selectedScheduledMissionIds])

  const toggleShowAllCleaning = () => {
    if (isAllCleaningSelected) {
      setSelectedScheduledMissionIds(new Set())
    } else {
      const allIds = new Set<number>()
      scheduledTasks.forEach(task => task.missions.forEach(id => allIds.add(id)))
      setSelectedScheduledMissionIds(allIds)
    }
  }

  const coloredScheduledCleaningRoutes = useMemo(() => {
    return scheduledMissionsDetails.flatMap(m =>
      m.cleaningRoutes.map(route => ({
        ...route,
        properties: {
          ...route.properties,
          highlighted: highlightedMissionIds.includes(m.mission.id),
        },
      }))
    )
  }, [scheduledMissionsDetails, highlightedMissionIds])

  // Assets tab state
  const [robotsPage, setRobotsPage] = useState(1)
  const [robotsPageInput, setRobotsPageInput] = useState('1')

  // Scenarios tab state
  const navigate = useNavigate()
  const [scenarios, setScenarios] = useState<any[]>([])
  const [scenariosProgress, setScenariosProgress] = useState(PROGRESS_TYPES.IDLE)
  const [highlightedScenarioId, setHighlightedScenarioId] = useState<number | undefined>(undefined)
  const [gausiumPreviewDismissed, setGausiumPreviewDismissed] = useState(false)
  const mapColumnRef = useRef<HTMLDivElement>(null)
  const [scenariosPage, setScenariosPage] = useState(1)
  const [scenariosPageInput, setScenariosPageInput] = useState('1')
  const [scenariosTotalCount, setScenariosTotalCount] = useState(0)
  const [scenariosRefreshKey, setScenariosRefreshKey] = useState(0)
  const hasGausiumRobots = useMemo(() => (robots ?? []).some((r: any) => r.vendor === 'GAUSIUM'), [robots])
  const hasAutonomicsRobots = useMemo(() => (robots ?? []).some((r: any) => r.vendor === 'AUTONOMICS'), [robots])
  const showGausiumMapsInScenarios = hasGausiumRobots && !hasAutonomicsRobots

  useEffect(() => {
    if (showGausiumMapsInScenarios && activeTab === 3) {
      setActiveTab(2)
    }
  }, [showGausiumMapsInScenarios, activeTab, setActiveTab])

  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false)
  const [launchItem, setLaunchItem] = useState<{ scenario?: any; mission?: IMissionInfo; task?: any } | undefined>(
    undefined
  )
  const [taskToEdit, setTaskToEdit] = useState<any | undefined>(undefined)
  const [initialTab, setInitialTab] = useState<number | undefined>(undefined)

  useEffect(() => {
    setScenariosPageInput(scenariosPage.toString())
  }, [scenariosPage])

  useEffect(() => {
    setRobotsPageInput(robotsPage.toString())
  }, [robotsPage])

  const handleScenariosPageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(scenariosPageInput)
      const maxPage = Math.ceil(scenariosTotalCount / PAGE_SIZE) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        setScenariosPage(val)
      } else {
        setScenariosPageInput(scenariosPage.toString())
      }
    }
  }

  const handleRobotsPageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(robotsPageInput)
      const maxPage = Math.ceil(robots.length / PAGE_SIZE) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        setRobotsPage(val)
      } else {
        setRobotsPageInput(robotsPage.toString())
      }
    }
  }

  useEffect(() => {
    if (activeTab !== 2) return
    let cancelled = false
    const groupIds: number[] = facility.groups_ids ?? []
    const run = async () => {
      try {
        setScenariosProgress(PROGRESS_TYPES.WORK)
        let allScenarios: any[] = []
        let totalCount = 0
        if (showGausiumMapsInScenarios) {
          const gsRobotIds = (robots ?? []).filter((r: any) => r.vendor === 'GAUSIUM').map((r: any) => r.id)
          const { results } = await MainApi.fetchGausiumMaps(gsRobotIds)
          allScenarios = results.map(m => {
            const partitions = (m.subareas_json?.partitions as any[]) || []
            const subareas = partitions
              .map((p: any) => String(p?.name ?? '').trim())
              .filter((v: string) => !!v)
            return {
              id: m.id,
              name: m.map_name || '(no name)',
              missions_ids: subareas,
              missions_count: subareas.length,
              missions_statuses: ['ok'],
              robot_type: m.robot_type || '',
              is_gausium_map: true,
              preview_url: m.preview_url ?? null,
            }
          })
          totalCount = allScenarios.length
        } else if (groupIds.length > 0) {
          const seenIds = new Set<number>()
          const promises = groupIds.map(gid =>
            MainApi.fetchScenarios(scenariosPage, PAGE_SIZE, undefined, undefined, gid).catch(err => {
              if (err?.status === 404 || err?.response?.status === 404) return { results: [], count: 0 }
              throw err
            })
          )
          const fetched = await Promise.all(promises)
          for (const { results, count } of fetched) {
            for (const s of results) {
              if (!seenIds.has(s.id)) {
                seenIds.add(s.id)
                allScenarios.push(s)
              }
            }
            totalCount = Math.max(totalCount, count)
          }
        } else {
          const { results, count } = await MainApi.fetchScenarios(scenariosPage, PAGE_SIZE)
          allScenarios = results
          totalCount = count
        }
        if (!cancelled) {
          setScenarios(allScenarios)
          setScenariosTotalCount(totalCount)
          setScenariosProgress(PROGRESS_TYPES.SUCCESS)
        }
      } catch (error) {
        if (!cancelled) setScenariosProgress(PROGRESS_TYPES.ERROR)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [activeTab, facility.groups_ids, scenariosPage, scenariosRefreshKey, showGausiumMapsInScenarios, robots])

  useEffect(() => {
    if (activeTab === 0 && odysseys.length > 0) {
      const scenarioIds = Array.from(new Set(odysseys.map(o => o.scenario).filter(Boolean))) as number[]
      if (scenarioIds.length > 0) {
        const fetchRefs = async () => {
          try {
            const promises = scenarioIds.map(id => MainApi.fetchScenario(id))
            const results = await Promise.all(promises)
            setReferencedScenarios(results)
          } catch (e) {
            console.warn('[FacilityView] fetch referenced scenarios error:', e)
          }
        }
        fetchRefs()
      }
    }
  }, [activeTab, odysseys])

  useEffect(() => {
    if (activeTab === 0 && odysseys.length > 0 && !hasInitializedCleaningMissions.current) {
      const scenarioIds = odysseys.map(o => o.scenario).filter(Boolean)
      if (scenarioIds.length > 0 && referencedScenarios.length === 0) {
        return
      }

      const scheduledMissionIds = new Set<number>()
      odysseys.forEach(odyssey => {
        const weekdays = odyssey.scheduler_settings?.weekdays
        if (weekdays && Object.values(weekdays).some(val => val && val.trim().length > 0)) {
          if (odyssey.scenario) {
            const scenario = referencedScenarios.find(s => s.id === odyssey.scenario)
            if (scenario && scenario.missions_ids) {
              scenario.missions_ids.forEach((mId: number) => scheduledMissionIds.add(mId))
            }
          } else if (odyssey.missions) {
            odyssey.missions.forEach((missionId: number) => scheduledMissionIds.add(missionId))
          }
        }
      })
      setSelectedScheduledMissionIds(scheduledMissionIds)
      hasInitializedCleaningMissions.current = true
    }
  }, [activeTab, odysseys, referencedScenarios])

  const onMapInited = () => {
    const m = mapRef.current?.getMap()
    if (m) setMap(m)
  }

  const formatType = (type: string) => type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ')

  const selectedGausiumScenarioPreview = useMemo(() => {
    if (activeTab !== 2 || !highlightedScenarioId) return null
    const s = scenarios.find(x => x.id === highlightedScenarioId)
    if (!s?.is_gausium_map) return null
    return { url: s.preview_url as string | null | undefined, name: String(s.name || '') }
  }, [activeTab, highlightedScenarioId, scenarios])

  useEffect(() => {
    setGausiumPreviewDismissed(false)
  }, [highlightedScenarioId, activeTab])

  const address = [facility.street_address, facility.city, facility.country].filter(Boolean).join(', ')

  useEffect(() => {
    if (activeTab !== 0 && activeTab !== 3) return
    const groupIds: number[] = facility.groups_ids ?? []
    let cancelled = false

    const run = async () => {
      try {
        setMissionsProgress(PROGRESS_TYPES.WORK)

        if (groupIds.length > 0) {
          const allMissions: IMissionInfo[] = []
          const seenIds = new Set<number>()
          let totalCount = 0
          const promises = groupIds.map(gid =>
            MainApi.fetchMissions(missionsPage, PAGE_SIZE, undefined, undefined, gid).catch(err => {
              // API returns 404 if the page doesn't exist for a specific group
              if (err?.status === 404 || err?.status_code === 404 || err?.response?.status === 404) {
                return { results: [], count: 0 }
              }
              throw err
            })
          )
          const fetchedResults = await Promise.all(promises)

          if (!cancelled) {
            for (const { results, count } of fetchedResults) {
              for (const m of results) {
                if (!seenIds.has(m.id)) {
                  seenIds.add(m.id)
                  allMissions.push(m)
                }
              }
              // We use max count as a heuristic for the total unique missions when groups are merged
              totalCount = Math.max(totalCount, count)
            }
            setMissions(allMissions)
            setMissionsTotalCount(totalCount)
          }
        } else {
          const { results, count } = await MainApi.fetchMissions(missionsPage, PAGE_SIZE)
          if (!cancelled) {
            setMissions(results)
            setMissionsTotalCount(count)
          }
        }

        if (!cancelled) setMissionsProgress(PROGRESS_TYPES.SUCCESS)
      } catch (error) {
        if (!cancelled) setMissionsProgress(PROGRESS_TYPES.ERROR)
        console.warn('[FacilityView] fetchMissions error:', error)
      }
    }

    run()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, missionsPage, facility.groups_ids, missionsRefreshKey])

  const fetchMissions = useCallback(async () => {
    setMissionsPage(1)
    setMissionsRefreshKey(k => k + 1)
  }, [])

  useEffect(() => {
    setMissionsPageInput(missionsPage.toString())
  }, [missionsPage])

  const handleMissionsPageJump = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const val = parseInt(missionsPageInput)
      const maxPage = Math.ceil(missionsTotalCount / PAGE_SIZE) || 1
      if (!isNaN(val) && val >= 1 && val <= maxPage) {
        setMissionsPage(val)
      } else {
        setMissionsPageInput(missionsPage.toString())
      }
    }
  }

  return (
    <div className={style.container}>
      <div className={style.titleBlock}>
        <div className={style.titleLeft}>
          <Title text={facility.name} />
          {address && <span className={style.address}>{address}</span>}
        </div>
        {facility.facility_type && <span className={style.badge}>{formatType(facility.facility_type)}</span>}
      </div>

      <div className={style.main}>
        <Box className={style.box}>
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => {
              setActiveTab(v)
              setHighlightedMissionIds([])
              setHighlightedRobotId(undefined)
              setSelectedMissionsDetails([])
              if (v === 0) {
                hasInitializedCleaningMissions.current = false
              }
            }}
            variant="fullWidth"
            indicatorColor="primary"
            sx={{ height: 45, minHeight: 45 }}
          >
            <StyledTab
              label="Cleaning"
              sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
              disableRipple
            />
            <StyledTab
              label="Assets"
              sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
              disableRipple
            />
            <StyledTab
              label="Scenarios"
              sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
              disableRipple
            />
            <StyledTab
              label="Missions"
              disabled={showGausiumMapsInScenarios}
              sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
              disableRipple
            />
          </Tabs>

          {/* Tab content */}
          {activeTab === 0 && (
            <div className={style.missionsTab} onClick={() => setHighlightedMissionIds([])}>
              {missionsProgress === PROGRESS_TYPES.WORK && <Loader />}
              {missionsProgress === PROGRESS_TYPES.ERROR && (
                <div className={style.emptyWrapper}>
                  <div className={style.emptyTitle}>Error loading scheduled missions</div>
                </div>
              )}
              {missionsProgress === PROGRESS_TYPES.SUCCESS &&
                (() => {
                  return (
                    <>
                      <MissionStats
                        coverage={aggregateStats.coverage}
                        length={aggregateStats.length}
                        time={aggregateStats.time}
                      />
                      <div className={style.cleaningTableHeader}>
                        <div></div>
                        <div className={clsx(style.missionsHeaderCol, 'text-16-400')}>Task</div>
                        <div className={clsx(style.missionsHeaderCol, 'text-16-400')}>Mission/Scenario</div>
                        <div className={clsx(style.missionsHeaderCol, 'text-16-400')}>Schedule</div>
                        <div className={clsx(style.missionsHeaderColCentered, 'text-16-400')}>
                          <input
                            type="checkbox"
                            checked={isAllCleaningSelected}
                            onChange={toggleShowAllCleaning}
                            onClick={e => e.stopPropagation()}
                          />
                        </div>
                        <div></div>
                      </div>
                      {scheduledTasks.length > 0 ? (
                        <div className={style.table}>
                          {scheduledTasks.map((task: any) => {
                            const uniqueKey = `odyssey_${task.id}`
                            const missionIds = task.missions || []
                            const isChecked =
                              missionIds.length > 0 &&
                              (missionIds as number[]).every((mId: number) => selectedScheduledMissionIds.has(mId))

                            return (
                              <FacilityCleaningTaskItem
                                key={uniqueKey}
                                task={{ ...task, isOdyssey: true } as any}
                                scheduledMissionsDetails={scheduledMissionsDetails}
                                referencedScenarios={referencedScenarios}
                                onClick={() => {
                                  if (missionIds.length > 0) {
                                    setHighlightedMissionIds(missionIds)
                                  }
                                }}
                                highlighted={
                                  missionIds.length > 0 &&
                                  (missionIds as number[]).every((id: number) => highlightedMissionIds.includes(id))
                                }
                                showCheckbox={true}
                                checked={isChecked}
                                onCheckboxChange={(checked: boolean) => {
                                  const newSet = new Set(selectedScheduledMissionIds)
                                  missionIds.forEach((mId: number) => {
                                    if (checked) newSet.add(mId)
                                    else newSet.delete(mId)
                                  })
                                  setSelectedScheduledMissionIds(newSet)
                                }}
                                onLaunch={task => {
                                  setLaunchItem({ task })
                                  setIsLaunchModalOpen(true)
                                }}
                                onReschedule={task => {
                                  setTaskToEdit(task)
                                  setInitialTab(1)
                                  setIsLaunchModalOpen(true)
                                }}
                                isOverlapping={task.isOverlapping}
                              />
                            )
                          })}
                        </div>
                      ) : (
                        <div className={style.emptyWrapper}>
                          <div className={style.emptyTitle}>No scheduled cleaning tasks</div>
                          <div className={style.emptyDescription}>
                            Create a scheduled task or Odyssey to see them here.
                          </div>
                        </div>
                      )}
                    </>
                  )
                })()}
            </div>
          )}

          {activeTab === 1 && (
            <div className={style.missionsTab}>
              <div className={style.assetsTableHeader}>
                <div></div>
                <div className={clsx(style.assetsHeaderCol, 'text-16-400')}>{t('robots.serial_id')}</div>
                <div className={clsx(style.assetsHeaderCol, 'text-16-400')}>{t('robots.name')}</div>
                <div className={clsx(style.assetsHeaderCol, 'text-16-400')}>{t('robots.battery')}</div>
                <div className={clsx(style.assetsHeaderColCentered, 'text-16-400')}>{t('robots.mode')}</div>
                <div></div>
              </div>
              {robots.length > 0 ? (
                <>
                  <div className={style.table}>
                    {robots.slice((robotsPage - 1) * PAGE_SIZE, robotsPage * PAGE_SIZE).map(robot => (
                      <RobotItem
                        key={robot.id}
                        robot={robot}
                        stations={stations}
                        onRobotUpdated={onRobotsUpdated}
                        hightlighted={robot.id === highlightedRobotId}
                        onStationsChanged={onStationsChanged}
                        robots={robots}
                      />
                    ))}
                  </div>
                  {/* Pagination controls for Assets */}
                  <div className={style.pagination}>
                    <button
                      className={style.paginationButton}
                      onClick={() => setRobotsPage(prev => Math.max(prev - 1, 1))}
                      disabled={robotsPage === 1}
                    >
                      &larr;
                    </button>
                    <span className={style.pageInfo}>
                      <input
                        className={style.pageInput}
                        value={robotsPageInput}
                        onChange={e => setRobotsPageInput(e.target.value)}
                        onKeyDown={handleRobotsPageJump}
                        onBlur={() => setRobotsPageInput(robotsPage.toString())}
                      />
                      / {Math.ceil(robots.length / PAGE_SIZE) || 1}
                    </span>
                    <button
                      className={style.paginationButton}
                      onClick={() => setRobotsPage(prev => prev + 1)}
                      disabled={robotsPage * PAGE_SIZE >= robots.length}
                    >
                      &rarr;
                    </button>
                  </div>
                </>
              ) : (
                <div className={style.emptyWrapper}>
                  <div className={style.emptyTitle}>No robots attached</div>
                  <div className={style.emptyDescription}>Add robots to this facility to see them here.</div>
                </div>
              )}
            </div>
          )}

          {activeTab === 2 && (
            <div className={style.missionsTab}>
              {scenariosProgress === PROGRESS_TYPES.WORK && <Loader />}
              {scenariosProgress === PROGRESS_TYPES.ERROR && (
                <div className={style.emptyWrapper}>
                  <div className={style.emptyTitle}>Error loading scenarios</div>
                </div>
              )}
              {scenariosProgress === PROGRESS_TYPES.SUCCESS && (
                <>
                  <ScenarioTable
                    className={style.scenariosTableRoot}
                    scenarios={scenarios}
                    highlightedScenarioId={highlightedScenarioId}
                    onScenarioClick={(id, missionIds) => {
                      setHighlightedScenarioId(id)
                      setHighlightedMissionIds(missionIds.filter((v): v is number => typeof v === 'number'))
                    }}
                    onLaunchScenario={scenario => {
                      if (scenario.is_gausium_map) return
                      setLaunchItem({ scenario })
                      setIsLaunchModalOpen(true)
                    }}
                    onScenariosUpdated={() => {
                      setScenariosPage(1)
                      setScenariosRefreshKey(k => k + 1)
                    }}
                  />
                  {/* Pagination controls */}
                  {!showGausiumMapsInScenarios && (
                    <div className={style.pagination}>
                      <button
                        className={style.paginationButton}
                        onClick={() => setScenariosPage(prev => Math.max(prev - 1, 1))}
                        disabled={scenariosPage === 1}
                      >
                        &larr;
                      </button>
                      <span className={style.pageInfo}>
                        <input
                          className={style.pageInput}
                          value={scenariosPageInput}
                          onChange={e => setScenariosPageInput(e.target.value)}
                          onKeyDown={handleScenariosPageJump}
                          onBlur={() => setScenariosPageInput(scenariosPage.toString())}
                        />
                        / {Math.ceil(scenariosTotalCount / PAGE_SIZE) || 1}
                      </span>
                      <button
                        className={style.paginationButton}
                        onClick={() => setScenariosPage(prev => prev + 1)}
                        disabled={scenariosPage * PAGE_SIZE >= scenariosTotalCount}
                      >
                        &rarr;
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 3 && (
            <div className={style.missionsTab}>
              {missionsProgress === PROGRESS_TYPES.WORK && <Loader />}
              {missionsProgress === PROGRESS_TYPES.ERROR && (
                <div className={style.emptyWrapper}>
                  <div className={style.emptyTitle}>Error loading missions</div>
                </div>
              )}
              {missionsProgress === PROGRESS_TYPES.SUCCESS && (
                <>
                  {/* Table header */}
                  <div className={style.missionsTableHeader}>
                    <div></div>
                    <div className={clsx(style.missionsHeaderCol, 'text-16-400')}>{t('missions.id')}</div>
                    <div className={clsx(style.missionsHeaderCol, 'text-16-400')}>{t('missions.name')}</div>
                    <div className={clsx(style.missionsHeaderColCentered, 'text-16-400')}>{t('missions.tasks')}</div>
                    <div className={clsx(style.missionsHeaderColCentered, 'text-16-400')}>
                      {t('missions.localization')}
                    </div>
                    <div />
                  </div>
                  {missions.length > 0 ? (
                    <div className={style.table}>
                      {missions.map(mission => (
                        <SharedMissionItem
                          key={mission.id}
                          mission={mission}
                          tasks={tasks}
                          odysseys={odysseys}
                          onMissionsDeleted={deletedId => {
                            if (deletedId) {
                              setHighlightedMissionIds(prev => prev.filter(id => id !== deletedId))
                            }
                            fetchMissions()
                          }}
                          onClick={() => setHighlightedMissionIds([mission.id])}
                          onLaunch={m => {
                            setLaunchItem({ mission: m })
                            setIsLaunchModalOpen(true)
                          }}
                          highlighted={highlightedMissionIds.includes(mission.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={style.emptyWrapper}>
                      <div className={style.emptyTitle}>No missions found</div>
                    </div>
                  )}
                  {/* Pagination controls */}
                  <div className={style.pagination}>
                    <button
                      className={style.paginationButton}
                      onClick={() => setMissionsPage(prev => Math.max(prev - 1, 1))}
                      disabled={missionsPage === 1}
                    >
                      &larr;
                    </button>
                    <span className={style.pageInfo}>
                      <input
                        className={style.pageInput}
                        value={missionsPageInput}
                        onChange={e => setMissionsPageInput(e.target.value)}
                        onKeyDown={handleMissionsPageJump}
                        onBlur={() => setMissionsPageInput(missionsPage.toString())}
                      />
                      / {Math.ceil(missionsTotalCount / PAGE_SIZE) || 1}
                    </span>
                    <button
                      className={style.paginationButton}
                      onClick={() => setMissionsPage(prev => prev + 1)}
                      disabled={missionsPage * PAGE_SIZE >= missionsTotalCount}
                    >
                      &rarr;
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </Box>

        {/* Map */}
        <div className={style.mapColumn} ref={mapColumnRef}>
          <MapboxCommon
            ref={mapRef}
            disableRuler={false}
            overlayItems={[]}
            onMapInited={onMapInited}
            onClick={e => {
              if (!e.features || e.features.length === 0) {
                setHighlightedMissionIds([])
              }
            }}
          >
            <StationsOnMap map={map} stations={stations.filter(s => s.point).map(s => s.point) as Point[]} />
            {activeTab === 0 && coloredScheduledCleaningRoutes.length > 0 && (
              <>
                {/* <CleaningZonesOnMap cleaningZones={scheduledMissionsDetails.flatMap(m => m.cleaningZones)} /> */}
                <CleaningRoutesOnMap
                  cleaningRoutes={coloredScheduledCleaningRoutes}
                  hasSelection={highlightedMissionIds.length > 0}
                />
              </>
            )}
            <AllRobotsOnMap map={map} robots={activeTab === 1 ? robots : []} onRobotClicked={setHighlightedRobotId} />
            {(activeTab === 2 || activeTab === 3) && selectedMissionsDetails.length > 0 && (
              <>
                <RestrictZonesOnMap restrictZones={selectedMissionsDetails.flatMap(m => m.restrictZones)} />
                {/* <CleaningZonesOnMap cleaningZones={selectedMissionsDetails.flatMap(m => m.cleaningZones)} /> */}
                <CleaningRoutesOnMap cleaningRoutes={selectedMissionsDetails.flatMap(m => m.cleaningRoutes)} />
                <TransferRoutesOnMap
                  transferRoutes={selectedMissionsDetails.flatMap(m => [...m.transferRoutes, ...(m.pointRoutes || [])])}
                />
                <CleaningPointsOnMap
                  cleaningRoutes={selectedMissionsDetails.flatMap(m => m.cleaningRoutes)}
                  launchPoints={selectedMissionsDetails.flatMap(m => m.launchPoints)}
                />
                <MissionStartFinishPoints
                  startPoints={selectedMissionsDetails.map(m => m.startingPoint)}
                  finishPoints={selectedMissionsDetails.map(m => m.finishingPoint)}
                />
              </>
            )}
          </MapboxCommon>
          {activeTab === 2 && (
            <GausiumMapPreviewOverlay
              visible={!!selectedGausiumScenarioPreview && !gausiumPreviewDismissed}
              previewUrl={selectedGausiumScenarioPreview?.url}
              mapName={selectedGausiumScenarioPreview?.name}
              mapWidgetRootRef={mapColumnRef}
              onClose={() => setGausiumPreviewDismissed(true)}
            />
          )}
        </div>
      </div>

      <TaskSettingsModal
        isOpen={isLaunchModalOpen}
        taskName=""
        onClose={() => {
          setIsLaunchModalOpen(false)
          setLaunchItem(undefined)
          setTaskToEdit(undefined)
          setInitialTab(undefined)
        }}
        onSubmit={() => {
          setIsLaunchModalOpen(false)
          setLaunchItem(undefined)
          setTaskToEdit(undefined)
          setInitialTab(undefined)
          setScenariosRefreshKey(k => k + 1)
          onRobotsUpdated()
        }}
        taskToEdit={taskToEdit}
        initialTab={initialTab}
        dateToEdit={null}
        initialLaunchItem={launchItem}
        robotIdsFilter={robots.map(r => r.id)}
      />
    </div>
  )
}

export default FacilityView
